import { useCallback, useState } from 'react';

import mapWithConcurrency from './mapWithConcurrency';

/**
 * React adapter around `mapWithConcurrency` that tracks live progress so callers
 * can render a "X of Y" indicator while a concurrency-capped batch runs.
 *
 * Usage:
 *   const [run, { completed, total, running }] = useConcurrentMap();
 *   await run(items, limit, async item => { ... });
 *
 * `run` resolves to the results array (input order) and rejects if an iteratee
 * rejects — same contract as `mapWithConcurrency`.
 */
export default function useConcurrentMap() {
  const [completed, setCompleted] = useState(0);
  const [total, setTotal] = useState(0);
  const [running, setRunning] = useState(false);

  const run = useCallback(async (items, limit, iteratee) => {
    setTotal(items.length);
    setCompleted(0);
    setRunning(true);

    try {
      return await mapWithConcurrency(items, limit, iteratee, setCompleted);
    } finally {
      setRunning(false);
    }
  }, []);

  return [run, { completed, total, running }];
}
